import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { VentaDetalle, VentaCreateRequest } from '../types/venta.types';
import { VentaService } from '../services/ventaService';

interface VentaMovimientoState {
  ventaDetalle: VentaDetalle | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  fetchVenta: (id: number) => Promise<void>;
  createVenta: (data: VentaCreateRequest) => Promise<any>;
  updateVenta: (id: number, data: VentaCreateRequest) => Promise<any>;
  anularVenta: (id: number) => Promise<boolean>;
  clearVenta: () => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  ventaDetalle: null,
  loading: false,
  saving: false,
  error: null,
};

export const useVentaMovimientoStore = create<VentaMovimientoState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchVenta: async (id: number) => {
        set({ loading: true, error: null }, false, 'ventaMovimiento/fetch/start');
        try {
          const detalle = await VentaService.getById(id);
          set({ ventaDetalle: detalle, loading: false }, false, 'ventaMovimiento/fetch/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar venta' }, false, 'ventaMovimiento/fetch/error');
        }
      },

      createVenta: async (data: VentaCreateRequest) => {
        set({ saving: true, error: null }, false, 'ventaMovimiento/create/start');
        try {
          const result = await VentaService.create(data);
          set({ saving: false }, false, 'ventaMovimiento/create/success');
          return result;
        } catch (error) {
          set({ saving: false, error: error instanceof Error ? error.message : 'Error al crear venta' }, false, 'ventaMovimiento/create/error');
          throw error;
        }
      },

      updateVenta: async (id: number, data: VentaCreateRequest) => {
        set({ saving: true, error: null }, false, 'ventaMovimiento/update/start');
        try {
          const result = await VentaService.update(id, data);
          set({ saving: false }, false, 'ventaMovimiento/update/success');
          return result;
        } catch (error) {
          set({ saving: false, error: error instanceof Error ? error.message : 'Error al actualizar venta' }, false, 'ventaMovimiento/update/error');
          throw error;
        }
      },

      anularVenta: async (id: number) => {
        set({ saving: true, error: null }, false, 'ventaMovimiento/anular/start');
        try {
          const result = await VentaService.anular(id);
          set({ saving: false }, false, 'ventaMovimiento/anular/success');
          return result;
        } catch (error) {
          set({ saving: false, error: error instanceof Error ? error.message : 'Error al anular venta' }, false, 'ventaMovimiento/anular/error');
          throw error;
        }
      },

      clearVenta: () => set({ ventaDetalle: null }, false, 'ventaMovimiento/clearVenta'),
      clearError: () => set({ error: null }, false, 'ventaMovimiento/clearError'),
      reset: () => set(initialState, false, 'ventaMovimiento/reset'),
    }),
    { name: 'venta-movimiento-store' }
  )
);
